import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePayment, usePayments, useRefundPayment } from '@/hooks/payments';
import { PaymentStatusBadge } from '@/components/payments/PaymentStatusBadge';
import { PaymentTimeline } from '@/components/payments/PaymentTimeline';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Eye, DollarSign } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { PaymentListItem } from '@/api/payments-api';

export function PaymentsPage() {
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR';
  const doctorId = (isDoctor ? (user as any).doctorId || user.id : '') || '';
  const patientId = user?.id || '';

  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [refundTarget, setRefundTarget] = useState<string | null>(null);
  const [refundAmount, setRefundAmount] = useState('');

  const { data: paymentList, isLoading: listLoading } = usePayments(
    isDoctor ? { doctorId } : { patientId }
  );
  const { data: selectedPayment, isLoading: detailLoading } = usePayment(selectedPaymentId || '');
  const { mutate: refund, isPending: isRefunding } = useRefundPayment();

  const formatCurrency = (value: number) => {
    const numeric = value > 1000 ? value / 100 : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numeric);
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  const handleRefund = () => {
    if (!refundTarget) return;
    const amount = refundAmount ? Math.round(parseFloat(refundAmount.replace(',', '.')) * 100) : undefined;
    refund(
      { paymentId: refundTarget, amount },
      { onSuccess: () => { setRefundTarget(null); setRefundAmount(''); } }
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {isDoctor ? 'Pagamentos & Recebíveis' : 'Pagamentos'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isDoctor ? 'Consulte as transações e pagamentos das suas consultas' : 'Consulte e gerencie seus pagamentos'}
        </p>
      </div>

      <div className="bg-card text-card-foreground rounded-2xl border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-foreground">Lista de Pagamentos</h2>
        </div>
        {listLoading ? (
          <div className="p-6 space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-xl" />
            ))}
          </div>
        ) : (paymentList?.items?.length ?? 0) === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-muted-foreground">Nenhum pagamento encontrado</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {paymentList?.items.map((p: PaymentListItem) => (
              <button
                key={p.paymentId}
                onClick={() => setSelectedPaymentId(p.paymentId)}
                className={cn(
                  'w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left',
                  selectedPaymentId === p.paymentId && 'bg-primary/10'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <DollarSign size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{formatCurrency(p.amount)}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(p.createdAt)} · {p.paymentMethod}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <PaymentStatusBadge status={p.status} />
                  <Eye size={16} className="text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedPayment && !detailLoading && (
        <div className="bg-card text-card-foreground rounded-2xl border border-border p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-bold text-foreground text-lg">Detalhes do Pagamento</h2>
              <p className="text-xs text-muted-foreground mt-0.5">ID: {selectedPayment.paymentId}</p>
            </div>
            <PaymentStatusBadge status={selectedPayment.status} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div className="p-3 bg-muted/40 rounded-xl border border-border">
              <span className="text-xs text-muted-foreground block">Valor</span>
              <p className="font-bold text-foreground text-base mt-0.5">
                {formatCurrency(selectedPayment.amount)}
              </p>
            </div>
            <div className="p-3 bg-muted/40 rounded-xl border border-border">
              <span className="text-xs text-muted-foreground block">Método</span>
              <p className="font-semibold text-foreground mt-0.5">{selectedPayment.paymentMethod || '-'}</p>
            </div>
            <div className="p-3 bg-muted/40 rounded-xl border border-border">
              <span className="text-xs text-muted-foreground block">Criado em</span>
              <p className="font-medium text-foreground mt-0.5 text-xs">
                {new Date(selectedPayment.createdAt).toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="p-3 bg-muted/40 rounded-xl border border-border">
              <span className="text-xs text-muted-foreground block">Atualizado em</span>
              <p className="font-medium text-foreground mt-0.5 text-xs">
                {new Date(selectedPayment.updatedAt).toLocaleString('pt-BR')}
              </p>
            </div>
          </div>

          <div className="pt-2">
            <h3 className="font-semibold text-foreground mb-3">Linha do Tempo</h3>
            <PaymentTimeline
              events={[
                { status: 'PENDING', label: 'Pagamento criado', date: selectedPayment.createdAt, active: true },
                {
                  status: selectedPayment.status === 'FAILED' ? 'FAILED' : 'APPROVED',
                  label: selectedPayment.status === 'FAILED' ? 'Pagamento falhou' : 'Pagamento aprovado',
                  date: selectedPayment.updatedAt,
                  active: selectedPayment.status !== 'PENDING',
                },
              ]}
            />
          </div>

          {!isDoctor && (selectedPayment.status === 'APPROVED' || selectedPayment.status === 'COMPLETED') && (
            <div className="pt-3 border-t border-border">
              <Button
                variant="secondary"
                onClick={() => setRefundTarget(selectedPayment.paymentId)}
              >
                Solicitar Reembolso
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!refundTarget} onOpenChange={(open) => { if (!open) setRefundTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Reembolso</DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Deseja reembolsar o pagamento <strong>{refundTarget}</strong>?
            </p>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Valor (opcional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">R$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Reembolso total"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value.replace(/[^0-9,]/g, ''))}
                  className="w-full pl-10 pr-4 py-2.5 border border-border bg-background text-foreground rounded-xl text-sm"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTarget(null)} disabled={isRefunding}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleRefund} disabled={isRefunding}>
              Confirmar Reembolso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
